// The city picker's pixel globe, drawn (site/js/ui/globe.js turns it and puts the labels on): a small canvas, scaled up
// without smoothing, redrawn every frame from the land map (globe-land.js). The Earth is lit by the real sun, with a
// dithered dusk band along the day/night line, city lights on the night side (denser on coasts and round the cities),
// a thin atmosphere, twinkling stars, and one pin per city.
import { LAND, LAND_W, LAND_H } from './globe-land.js';

export const RAD = Math.PI / 180;
/** A point on the unit sphere, from latitude and longitude in radians: x east at 90°E, y north, z out at 0°, 0° */
export const vec = (lat, lon) => [Math.cos(lat) * Math.sin(lon), Math.sin(lat), Math.cos(lat) * Math.cos(lon)];
const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const hash = (x, y) => { let h = (x * 374761393 + y * 668265263) | 0; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

/** Where the sun is straight overhead at `ms` (a unit vector; the equation of time is left out, within ~4° of longitude) */
export function sunAt(ms) {
  const d = new Date(ms), day = (ms - Date.UTC(d.getUTCFullYear(), 0, 0)) / 864e5;
  const decl = -23.44 * Math.cos((2 * Math.PI / 365) * (day + 10)) * RAD;
  const utcH = d.getUTCHours() + d.getUTCMinutes() / 60 + d.getUTCSeconds() / 3600;
  return vec(decl, (12 - utcH) * 15 * RAD);
}

/** A point on the Earth as seen with (lon0, lat0) facing us: u right, v up, z towards us (below 0: the far side) */
export function toView(p, lon0, lat0, out = {}) {
  const cl = Math.cos(lon0), sl = Math.sin(lon0), cp = Math.cos(lat0), sp = Math.sin(lat0);
  const x1 = p[0] * cl - p[2] * sl, z1 = p[0] * sl + p[2] * cl;
  out.u = x1; out.v = p[1] * cp - z1 * sp; out.z = p[1] * sp + z1 * cp;
  return out;
}

/** The land map as one cell per byte: 0 ocean, 1 land, 2 sand, 3 ice */
export function decodeLand(text = LAND) {
  const map = new Uint8Array(LAND_W * LAND_H);
  text.split('|').forEach((row, j) => {
    let i = j * LAND_W;
    for (const [, v, n] of row.matchAll(/([OGSI])([0-9a-z]+)/g)) { const len = parseInt(n, 36); map.fill('OGSI'.indexOf(v), i, i + len); i += len; }
  });
  return map;
}
/** The cell under a point, from latitude and longitude in degrees */
export const cellAt = (map, lat, lon) => {
  const i = ((Math.floor((lon / 360 + 0.5) * LAND_W) % LAND_W) + LAND_W) % LAND_W;
  const j = Math.min(LAND_H - 1, Math.max(0, Math.floor((0.5 - lat / 180) * LAND_H)));
  return map[j * LAND_W + i];
};

/**
 * The arrow keys on the globe: the city on screen next to `from` in direction [dx, dy] (screen pixels, y down), the
 * nearest one more or less that way (distance across it counts double), or null. `spots` are { id, x, y, z } with z the
 * facing (only those over 0.15, clear of the edge, count).
 */
export function nextCity(spots, from, [dx, dy]) {
  const a = spots.find(s => s.id === from);
  if (!a) return null;
  let best = null, score = Infinity;
  for (const s of spots) {
    if (s.id === from || s.z <= 0.15) continue;
    const ox = s.x - a.x, oy = s.y - a.y, along = ox * dx + oy * dy, across = Math.abs(ox * dy - oy * dx);
    if (along <= 0.5 || across > along * 2.5) continue;
    const k = along + 2 * across;
    if (k < score) { score = k; best = s.id; }
  }
  return best;
}

const px = h => { const n = parseInt(h.slice(1), 16); return ((255 << 24) | ((n & 255) << 16) | (((n >> 8) & 255) << 8) | (n >> 16)) >>> 0; };
const rgba = (h, a) => { const n = parseInt(h.slice(1), 16); return ((Math.round(a * 255) << 24) | ((n & 255) << 16) | (((n >> 8) & 255) << 8) | (n >> 16)) >>> 0; };
// dark (night, the far edge) to bright (noon), seven steps each
const RAMPS = [
  ['#060a1c', '#0a1230', '#0f1b44', '#15275a', '#1d3772', '#274a8a', '#335e9e'],   // ocean
  ['#0f1428', '#161f34', '#1d2e39', '#28453e', '#365c45', '#4a764d', '#638f56'],   // land
  ['#16152a', '#24203a', '#3a3142', '#5a4a4d', '#85694f', '#b08a5c', '#cfa96c'],   // sand
  ['#191d38', '#2a3150', '#444e6e', '#67718f', '#919bb5', '#bcc5da', '#e2e8f4'],   // ice
  ['#0b1530', '#11203f', '#183155', '#21456c', '#2c5b83', '#3a7299', '#4b88ab'],   // shallow water along the coasts
].map(r => r.map(px));
const DUSK = ['#3a2a5e', '#6a4052', '#8e5a4a', '#9a84b0', '#4a3468'].map(px);
const LIGHTS = ['#8a5a3a', '#d99a55', '#ffd98a'].map(px);
const AIR_DAY = '#8ec1ff', AIR_NIGHT = '#34438c', STAR = '#cfd6ff';
const OUTLINE = px('#070814'), CREAM = px('#f3e6c4'), CREAM_HI = px('#fffaf0'), WHITE = px('#ffffff');
const LIVE = px('#72e6a2'), LIVE_HI = px('#c8f7dc'), DIM = px('#6d7090');
// the city's accent, and halfway to white for the pin's lit corner
const lighter = h => `#${[1, 3, 5].map(i => Math.round((parseInt(h.slice(i, i + 2), 16) + 255) / 2).toString(16).padStart(2, '0')).join('')}`;
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => (v + 0.5) / 16);

/**
 * The drawing, on `ctx` (a canvas N px square, set by resize). `cities` are [{ v }] (vec) for the lights round them.
 * draw(view, now): view = { lon0, lat0, R (the globe's radius in canvas px), sun, pins: [{ x, y, z, kind }] } with pins
 * in canvas px, drawn in order; kind 'here' (the city on screen: its `accent`, a #rrggbb, and a ring unless still), 'big'
 * (hovered or picked), 'top' (most people right now), 'off' (not in the search) or 'city'. `still` stops the ring and the
 * twinkling.
 */
export function createGlobeArt(ctx, cities) {
  const map = decodeLand();
  // City lights: points, not map cells, so each stays one steady pixel as the globe turns. A land cell gets one with a
  // chance of 0.4 within 2.2° of a city, 0.05 on a coast, 0.006 inland (a third of that on sand); the dice (hash) are
  // thrown first, so most cells need no more checking.
  const lights = [], near = Math.cos(2.2 * RAD);
  for (let j = 0; j < LAND_H; j++) for (let i = 0; i < LAND_W; i++) {
    const t = map[j * LAND_W + i], roll = hash(i, j);
    if ((t !== 1 && t !== 2) || roll >= 0.4) continue;
    const lat = (90 - (j + 0.5) * (180 / LAND_H)) * RAD, lon = ((i + 0.5) * (360 / LAND_W) - 180) * RAD;
    if (Math.abs(lat) > 62 * RAD) continue;
    const v = vec(lat, lon);
    if (!cities.some(c => c.v[0] * v[0] + c.v[1] * v[1] + c.v[2] * v[2] > near)) {
      if (roll >= 0.05) continue;
      let coast = false;
      for (let dj = -2; dj <= 2 && !coast; dj++) for (let di = -2; di <= 2; di++) {
        const jj = Math.min(LAND_H - 1, Math.max(0, j + dj)), ii = (i + di + LAND_W) % LAND_W;
        if (map[jj * LAND_W + ii] === 0) { coast = true; break; }
      }
      if (roll >= (coast ? 0.05 : 0.006) * (t === 2 ? 0.3 : 1)) continue;
    }
    lights.push({ v, b: 0.45 + hash(j, i) * 0.55, ph: hash(i + 7, j + 3) * 6.283 });
  }
  const stars = Array.from({ length: 90 }, (_, k) => ({ x: hash(k, 1), y: hash(k, 2), b: 0.3 + hash(k, 3) * 0.7, ph: hash(k, 4) * 6.283, sp: 0.6 + hash(k, 5) * 1.8 }));
  let N = 0, img, buf, surf, dots, zs, clear, accent = '', ACCENT = 0, ACCENT_HI = 0;
  const P = {};

  function resize(n) {
    N = n;
    ctx.canvas.width = ctx.canvas.height = N;
    img = ctx.createImageData(N, N);
    buf = new Uint32Array(img.data.buffer);
    surf = new Uint8Array(N * N); dots = new Float32Array(N * N); zs = new Float32Array(N * N); clear = new Uint8Array(N * N);
  }
  // a pin's fill: n px square from (x0, y0), the city under it more or less in the middle
  const pinBox = (fx, fy, kind) => { const n = kind === 'big' ? 3 : 2; return { n, x0: Math.round(fx - n / 2), y0: Math.round(fy - n / 2) }; };

  function draw({ lon0, lat0, R, sun, pins, still, accent: a = '#ffc86e' }, now) {
    if (!N) return;
    if (a !== accent) { accent = a; ACCENT = px(a); ACCENT_HI = px(lighter(a)); }
    const c = N / 2, cl = Math.cos(lon0), sl = Math.sin(lon0), cp = Math.cos(lat0), sp = Math.sin(lat0);
    const tw = still ? 0 : now;
    buf.fill(0);
    for (const s of stars) {
      const a = s.b * (0.55 + 0.45 * Math.sin(tw * 0.001 * s.sp + s.ph));
      buf[Math.floor(s.y * N) * N + Math.floor(s.x * N)] = rgba(STAR, Math.max(0, a) * 0.8);
    }
    // pass 1: what's under each pixel and how the sun meets it; a 2.5 px atmosphere just outside the edge
    const rim = 2.5 / R;
    for (let y = 0; y < N; y++) {
      const v = (c - (y + 0.5)) / R;
      for (let x = 0; x < N; x++) {
        const u = ((x + 0.5) - c) / R, r2 = u * u + v * v, k = y * N + x;
        surf[k] = 0;
        if (r2 > 1) {
          if (r2 < (1 + rim) * (1 + rim)) {
            const r = Math.sqrt(r2), du = u / r, dv = v / r, y1 = dv * cp, z1 = -dv * sp;
            const d = (du * cl + z1 * sl) * sun[0] + y1 * sun[1] + (-du * sl + z1 * cl) * sun[2];
            const day = smooth(-0.3, 0.3, d), out = (r - 1) * R;
            buf[k] = rgba(day > 0.5 ? AIR_DAY : AIR_NIGHT, (out < 1 ? 0.7 : out < 2 ? 0.3 : 0.1) * (0.35 + 0.65 * day));
          }
          continue;
        }
        const z = Math.sqrt(1 - r2), y1 = v * cp + z * sp, z1 = -v * sp + z * cp;
        const X = u * cl + z1 * sl, Z = -u * sl + z1 * cl;
        const i = (Math.floor((Math.atan2(X, Z) / (2 * Math.PI) + 0.5) * LAND_W) + LAND_W) % LAND_W;
        const j = Math.min(LAND_H - 1, Math.floor((0.5 - Math.asin(y1) / Math.PI) * LAND_H));
        surf[k] = map[j * LAND_W + i] + 1;
        dots[k] = X * sun[0] + y1 * sun[1] + Z * sun[2];
        zs[k] = z;
      }
    }
    // pass 2: colour, dithered between the ramp's steps; water next to land is the shallow ramp
    for (let k = 0; k < N * N; k++) {
      if (!surf[k]) continue;
      let t = surf[k] - 1;
      if (t === 0 && (surf[k - 1] > 1 || surf[k + 1] > 1 || surf[k - N] > 1 || surf[k + N] > 1)) t = 4;
      const b = BAYER[(((k / N) | 0) & 3) * 4 + ((k % N) & 3)], d = dots[k], z = zs[k];
      const day = smooth(-0.1, 0.1, d), dayTone = 0.55 + 0.45 * Math.sqrt(Math.max(0, d)), nightTone = 0.3 + 0.1 * z;
      const tone = (nightTone + (dayTone - nightTone) * day) * (0.72 + 0.28 * z) + 0.14 * smooth(0.25, 0, z) * day;   // + haze at the lit edge
      const dusk = 1 - Math.min(1, Math.abs(d - 0.02) / 0.09);
      buf[k] = dusk > 0 && b < dusk * 0.55 ? DUSK[t] : RAMPS[t][Math.min(6, Math.max(0, Math.floor(tone * 6 + b - 0.25)))];
    }
    // The lights crowd round the cities, so each pin gets a ring kept clear of them (2 px past its outline): a pin
    // touching lights reads as one more of them.
    clear.fill(0);
    for (const { x: fx, y: fy, z, kind } of pins) {
      if (z <= 0.02) continue;
      const { n, x0, y0 } = pinBox(fx, fy, kind), cx = x0 + n / 2, cy = y0 + n / 2, r = n / 2 + 3;
      for (let y = Math.max(0, Math.floor(cy - r)); y < Math.min(N, Math.ceil(cy + r)); y++)
        for (let x = Math.max(0, Math.floor(cx - r)); x < Math.min(N, Math.ceil(cx + r)); x++)
          if ((x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2 < r * r) clear[y * N + x] = 1;
    }
    for (const l of lights) {
      toView(l.v, lon0, lat0, P);
      if (P.z < 0.1) continue;
      const night = 1 - smooth(-0.12, 0.04, l.v[0] * sun[0] + l.v[1] * sun[1] + l.v[2] * sun[2]);
      if (night <= 0) continue;
      const x = Math.floor(c + P.u * R), y = Math.floor(c - P.v * R);
      if (x < 0 || y < 0 || x >= N || y >= N) continue;
      const br = night * l.b * (0.78 + 0.22 * Math.sin(tw * 0.0017 + l.ph));
      if (br >= 0.2 && !clear[y * N + x]) buf[y * N + x] = LIGHTS[br > 0.72 ? 2 : br > 0.45 ? 1 : 0];
    }
    const put = (x, y, col) => { if (x >= 0 && y >= 0 && x < N && y < N) buf[y * N + x] = col; };
    for (const { x: fx, y: fy, z, kind } of pins) {
      if (z <= 0.02) continue;
      if (kind === 'here' && !still) {   // a ring going out
        const ph = (now % 1600) / 1600, rr = 3 + ph * 4;
        if (ph < 0.8) for (let a = 0; a < 48; a++) put(Math.round(fx - 0.5 + Math.cos(a / 48 * 6.283) * rr), Math.round(fy - 0.5 + Math.sin(a / 48 * 6.283) * rr), ACCENT);
      }
      const { n, x0, y0 } = pinBox(fx, fy, kind);
      const [fill, hi] = { here: [ACCENT, ACCENT_HI], big: [WHITE, WHITE], top: [LIVE, LIVE_HI], off: [DIM, DIM], city: [CREAM, CREAM_HI] }[kind];
      for (let yy = -1; yy <= n; yy++) for (let xx = -1; xx <= n; xx++) {
        const edgeX = xx < 0 || xx >= n, edgeY = yy < 0 || yy >= n;
        if (edgeX && edgeY) continue;   // the outline's corners, rounded off
        put(x0 + xx, y0 + yy, edgeX || edgeY ? OUTLINE : xx === 0 && yy === 0 ? hi : fill);
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  return { resize, draw };
}
